/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/components/transfer/index.tsx'.
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import List from './List';
import Operation from './Operation';
import Search from './Search';
import LocaleReceiver from 'antd/lib/locale-provider/LocaleReceiver';
import defaultLocale from 'antd/lib/locale/zh_CN';
import { ConfigConsumer } from '../config-provider';
import warning from 'antd/lib/_util/warning';
defaultLocale.Transfer.titles = ['', ''];
class Transfer extends React.Component {
    // For high-level customized Transfer @dqaria
    static List = List;
    static Operation = Operation;
    static Search = Search;
    static defaultProps = {
        dataSource: [],
        locale: {},
        showSearch: false,
        listStyle: () => {
            return;
        }
    };
    static getDerivedStateFromProps({ selectedKeys, targetKeys, pagination, children }) {
        if (selectedKeys) {
            const mergedTargetKeys = targetKeys || [];
            return {
                sourceSelectedKeys: selectedKeys.filter((key) => !mergedTargetKeys.includes(key)),
                targetSelectedKeys: selectedKeys.filter((key) => mergedTargetKeys.includes(key))
            };
        }
        if (process.env.NODE_ENV !== 'production')
            warning(!pagination || !children, 'Transfer', '`pagination` not support customize render list.');
        return null;
    }
    separatedDataSource = null;
    constructor(props) {
        super(props);
        const { selectedKeys = [], targetKeys = [] } = props;
        this.state = {
            sourceSelectedKeys: selectedKeys.filter((key) => targetKeys.indexOf(key) === -1),
            targetSelectedKeys: selectedKeys.filter((key) => targetKeys.indexOf(key) > -1)
        };
        warning(props.theme !== 'gray', 'Transfer', `'themes = gray' is deprecated now`);
    }
    setStateKeys = (direction, keys) => {
        if (direction === 'left') {
            this.setState(({ sourceSelectedKeys }) => ({
                sourceSelectedKeys: typeof keys === 'function' ? keys(sourceSelectedKeys || []) : keys
            }));
        }
        else {
            this.setState(({ targetSelectedKeys }) => ({
                targetSelectedKeys: typeof keys === 'function' ? keys(targetSelectedKeys || []) : keys
            }));
        }
    };
    getTitles(transferLocale) {
        return this.props.titles ?? transferLocale.titles;
    }
    getLocale = (transferLocale, renderEmpty) => ({
        ...transferLocale,
        notFoundContent: renderEmpty('Transfer'),
        ...this.props.locale
    });
    moveTo = (direction) => {
        const { targetKeys = [], dataSource = [], onChange } = this.props;
        const { sourceSelectedKeys, targetSelectedKeys } = this.state;
        const moveKeys = direction === 'right' ? sourceSelectedKeys : targetSelectedKeys;
        // filter the disabled options
        const newMoveKeys = moveKeys.filter((key) => !dataSource.some((data) => !!(key === data.key && data.disabled)));
        // move items to target box
        const newTargetKeys = direction === 'right'
            ? newMoveKeys.concat(targetKeys)
            : targetKeys.filter((targetKey) => newMoveKeys.indexOf(targetKey) === -1);
        // empty checked keys
        const oppositeDirection = direction === 'right' ? 'left' : 'right';
        this.setStateKeys(oppositeDirection, []);
        this.handleSelectChange(oppositeDirection, []);
        onChange?.(newTargetKeys, direction, newMoveKeys);
    };
    moveToLeft = () => this.moveTo('left');
    moveToRight = () => this.moveTo('right');
    onItemSelectAll = (direction, selectedKeys, checkAll) => {
        this.setStateKeys(direction, (prevKeys) => {
            let mergedCheckedKeys = [];
            if (checkAll) {
                // Merge current keys with origin key
                mergedCheckedKeys = Array.from(new Set([...prevKeys, ...selectedKeys]));
            }
            else {
                // Remove current keys from origin keys
                mergedCheckedKeys = prevKeys.filter((key) => selectedKeys.indexOf(key) === -1);
            }
            this.handleSelectChange(direction, mergedCheckedKeys);
            return mergedCheckedKeys;
        });
    };
    onLeftItemSelectAll = (selectedKeys, checkAll) => this.onItemSelectAll('left', selectedKeys, checkAll);
    onRightItemSelectAll = (selectedKeys, checkAll) => this.onItemSelectAll('right', selectedKeys, checkAll);
    handleFilter = (direction, e) => {
        const { onSearch } = this.props;
        const { value } = e.target;
        onSearch?.(direction, value);
    };
    handleLeftFilter = (e) => this.handleFilter('left', e);
    handleRightFilter = (e) => this.handleFilter('right', e);
    handleClear = (direction) => {
        const { onSearch } = this.props;
        onSearch?.(direction, '');
    };
    handleLeftClear = () => this.handleClear('left');
    handleRightClear = () => this.handleClear('right');
    onItemSelect = (direction, selectedKey, checked) => {
        const { sourceSelectedKeys, targetSelectedKeys } = this.state;
        const holder = direction === 'left' ? [...sourceSelectedKeys] : [...targetSelectedKeys];
        const index = holder.indexOf(selectedKey);
        if (index > -1) {
            holder.splice(index, 1);
        }
        if (checked) {
            holder.push(selectedKey);
        }
        this.handleSelectChange(direction, holder);
        if (!this.props.selectedKeys) {
            this.setStateKeys(direction, holder);
        }
    };
    onLeftItemSelect = (selectedKey, checked) => this.onItemSelect('left', selectedKey, checked);
    onRightItemSelect = (selectedKey, checked) => this.onItemSelect('right', selectedKey, checked);
    onRightItemRemove = (selectedKeys) => {
        const { targetKeys = [], onChange } = this.props;
        this.setStateKeys('right', []);
        onChange?.(targetKeys.filter((key) => !selectedKeys.includes(key)), 'left', [...selectedKeys]);
    };
    handleScroll = (direction, e) => {
        const { onScroll } = this.props;
        onScroll?.(direction, e);
    };
    handleLeftScroll = (e) => this.handleScroll('left', e);
    handleRightScroll = (e) => this.handleScroll('right', e);
    handleSelectChange(direction, holder) {
        const { sourceSelectedKeys, targetSelectedKeys } = this.state;
        const { onSelectChange } = this.props;
        if (!onSelectChange) {
            return;
        }
        if (direction === 'left') {
            onSelectChange(holder, targetSelectedKeys);
        }
        else {
            onSelectChange(sourceSelectedKeys, holder);
        }
    }
    handleListStyle = (listStyle, direction) => {
        if (typeof listStyle === 'function') {
            return listStyle({ direction });
        }
        return listStyle;
    };
    separateDataSource() {
        const { dataSource, rowKey, targetKeys = [] } = this.props;
        const leftDataSource = [];
        const rightDataSource = new Array(targetKeys.length);
        // @ts-ignore
        dataSource.forEach((record) => {
            if (rowKey) {
                record = {
                    ...record,
                    key: rowKey(record)
                };
            }
            // rightDataSource should be ordered by targetKeys
            // leftDataSource should be ordered by dataSource
            const indexOfKey = targetKeys.indexOf(record.key);
            if (indexOfKey !== -1) {
                rightDataSource[indexOfKey] = record;
            }
            else {
                leftDataSource.push(record);
            }
        });
        return {
            leftDataSource,
            rightDataSource
        };
    }
    renderTransfer = (transferLocale) => (React.createElement(ConfigConsumer, null, ({ getPrefixCls, renderEmpty, direction }) => {
        const { prefixCls: customizePrefixCls, className, disabled, operations = [], showSearch, footer, style, listStyle, operationStyle, filterOption, render, children, showSelectAll, oneWay, pagination } = this.props;
        const prefixCls = getPrefixCls('transfer', customizePrefixCls);
        const locale = this.getLocale(transferLocale, renderEmpty);
        const { sourceSelectedKeys, targetSelectedKeys } = this.state;
        const mergedPagination = !children && pagination;
        const { leftDataSource, rightDataSource } = this.separateDataSource();
        const leftActive = targetSelectedKeys.length > 0;
        const rightActive = sourceSelectedKeys.length > 0;
        const cls = classNames(prefixCls, {
            [`${prefixCls}-disabled`]: disabled,
            [`${prefixCls}-customize-list`]: !!children,
            [`${prefixCls}-rtl`]: direction === 'rtl'
        }, className);
        const titles = this.getTitles(locale);
        const selectAllLabels = this.props.selectAllLabels || [];
        return (React.createElement("div", { className: cls, style: style },
            React.createElement(List, { prefixCls: `${prefixCls}-list`, titleText: titles[0], dataSource: leftDataSource, filterOption: filterOption, style: this.handleListStyle(listStyle, 'left'), checkedKeys: sourceSelectedKeys, handleFilter: this.handleLeftFilter, handleClear: this.handleLeftClear, onItemSelect: this.onLeftItemSelect, onItemSelectAll: this.onLeftItemSelectAll, render: render, showSearch: showSearch, 
                // @ts-ignore
                renderList: children, 
                // @ts-ignore
                footer: footer, onScroll: this.handleLeftScroll, disabled: disabled, direction: direction === 'rtl' ? 'right' : 'left', showSelectAll: showSelectAll, selectAllLabel: selectAllLabels[0], pagination: mergedPagination, ...locale }),
            React.createElement(Operation, { className: `${prefixCls}-operation`, rightActive: rightActive, rightArrowText: operations[0], moveToRight: this.moveToRight, leftActive: leftActive, leftArrowText: operations[1], moveToLeft: this.moveToLeft, style: operationStyle, disabled: disabled, direction: direction, oneWay: oneWay }),
            React.createElement(List, { prefixCls: `${prefixCls}-list`, titleText: titles[1], dataSource: rightDataSource, filterOption: filterOption, style: this.handleListStyle(listStyle, 'right'), checkedKeys: targetSelectedKeys, handleFilter: this.handleRightFilter, handleClear: this.handleRightClear, onItemSelect: this.onRightItemSelect, onItemSelectAll: this.onRightItemSelectAll, onItemRemove: this.onRightItemRemove, render: render, showSearch: showSearch, 
                // @ts-ignore
                renderList: children, 
                // @ts-ignore
                footer: footer, onScroll: this.handleRightScroll, disabled: disabled, direction: direction === 'rtl' ? 'left' : 'right', showSelectAll: showSelectAll, selectAllLabel: selectAllLabels[1], showRemove: oneWay, pagination: mergedPagination, ...locale })));
    }));
    render() {
        return (
        // @ts-ignore
        React.createElement(LocaleReceiver, { componentName: 'Transfer', defaultLocale: defaultLocale.Transfer }, this.renderTransfer));
    }
}
export { Transfer };
export default Transfer;
